/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */

import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	isStoreAssistant,
	type StoreAssistant,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { getSendMessageExperienceName } from './getSendMessageExperienceName';
import { markInFlightActionsAsCancelled } from './markInFlightActionsAsCancelled';
import { setConversationData } from './setConversationData';
import type { ChatStoreAction } from './types';

type AbortRequestsArgs = {
	conversationId?: string;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
};

// Abort requests for a specific conversation, or ALL conversations if no conversationId is passed
export const abortRequests: SafeAction<[AbortRequestsArgs?]> = createSafeAction(
	StoreActionLoggableName.abortRequests,
	({
		conversationId,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		deepResearchEnabled,
	}: AbortRequestsArgs = {}): ChatStoreAction =>
		(
			{ getState, dispatch },
			{
				experienceTracker,
				setReasoningMode,
				isReconnecting,
				unifiedTasksActions,
				markIncompleteResearchesErrored,
				clearMessageResearch,
				getMessages,
				setMessages,
			},
		) => {
			const conversations = getState().conversations;

			const abortConversationStream = (conversation: Conversation) => {
				if (
					conversation?.id &&
					expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true)
				) {
					dispatch(
						setConversationData({
							conversationId: conversation.id,
							data: clearStatus({ lastAgentMessageStatus: 'CANCELLED' }),
						}),
					);

					// Only clear research when answer streaming has started.
					if (conversation.status === CHAT_STORE_STATUS.typingResponse) {
						const activeAssistantMessageIds = getMessages(conversation.id)
							.filter(
								(message): message is StoreAssistant =>
									isStoreAssistant(message) && message.content.streamingComplete !== true,
							)
							.map((message) => message.content.message.message.id);
						activeAssistantMessageIds.forEach((messageId) => {
							markIncompleteResearchesErrored({ messageId });
							clearMessageResearch({ messageId });
						});
					}
				}

				if (conversation?.storeAbortController) {
					experienceTracker.start({
						name: ExperienceName.ABORT_AGENT_STREAM,
						id: conversation?.id,
						attributes: {
							conversationId: conversation?.id,
							isReconnecting: isReconnecting(conversation?.id ?? ''),
						},
					});

					conversation.storeAbortController.abort();

					if (
						fg('cc-cwr-fix-finalizing-cancelled-request') &&
						conversation.id &&
						!isReconnecting(conversation.id)
					) {
						const currentMessages = getMessages(conversation.id);
						const nextMessages = markInFlightActionsAsCancelled(currentMessages);
						if (nextMessages !== currentMessages) {
							setMessages(conversation.id, nextMessages);
						}
					}

					experienceTracker.abort({
						name: getSendMessageExperienceName({
							deepResearchEnabled,
							thinkDeeperEnabled,
							taskModeEnabled,
							fastModeEnabled,
						}),
						reason: 'Aborted',
						attributes: {
							isProgrammaticAbort: true,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.ABORT_AGENT_STREAM,
					});

					if (
						taskModeEnabled &&
						conversation?.id &&
						unifiedTasksActions &&
						fg('rovo_chat_enable_tasks')
					) {
						unifiedTasksActions.setTaskProgressState({
							conversationId: conversation.id,
							progressState: 'Cancelled',
							updatedAt: new Date().toISOString(),
						});
					}
				}
			};

			if (conversationId) {
				abortConversationStream(conversations[conversationId]);
			} else {
				Object.values(conversations).forEach(abortConversationStream);
			}
		},
	{
		experienceName: () => ExperienceName.ABORT_AGENT_STREAM,
	},
);
